import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import './Layout.css';
import { getProjects, Project, getConversations, Conversation, updateProject, archiveProject, deleteConversation, updateConversation } from '../../services/api';
import AssistantWidget from '../Assistant/AssistantWidget';
import RestrictedBanner from '../Billing/RestrictedBanner';
import CRMWorkspace from '../CRMWorkspace';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
    Home,
    Palette,
    FileText,
    BookOpen,
    Globe,
    TrendingUp,
    Settings,
    Users,
    User,
    Calendar,
    Rocket,
    HelpCircle,
    Film,
    Menu,
    Moon,
    Sun,
    X,
    ChevronRight,
    ChevronLeft,
    ChevronDown,
    ChevronUp,
    LogOut,
    Plus,
    MessageSquare,
    RotateCcw,
    Trash2,
    Megaphone,
    Search,
    Share2,
    BarChart2,
    UserPlus,
    Image,
    Star,
    Bot,
    Radar,
    Store,
    Cpu,
    Blocks,
    Mail,
    CalendarDays,
    CalendarClock,
    Plug,
    Link2,
    ShoppingBag,
    Lock,
    Zap,
    Server,
    KeyRound,
    LifeBuoy,
    Gauge,
    ClipboardList
} from 'lucide-react';
import NotificationCentre from '../Notifications/NotificationCentre';
import NavFlyout from './NavFlyout';

// Define the core menu structure with Lucide icons
interface NavItem {
    name: string;
    path: string;
    icon: React.FC<any>;
    moduleKey: string;
    requiresProject?: boolean;
    adminOnly?: boolean;
    children?: NavItem[];
    expandOnly?: boolean;  // parent only toggles its submenu on click (does not navigate to its own path)
}

interface NavSection {
    label: string;
    icon: React.FC<any>;
    items: NavItem[];
    defaultExpanded?: boolean;
}

const navSections: NavSection[] = [
    {
        label: 'Core & Vitals',
        icon: Gauge,
        defaultExpanded: true,
        items: [
            { name: 'Dashboard', path: '/dashboard', icon: Home, moduleKey: 'dashboard' },
            { name: 'Analytics', path: '/analytics', icon: BarChart2, moduleKey: 'analytics' },
            { name: 'Predictions', path: '/predictions', icon: TrendingUp, moduleKey: 'analytics' },
            { name: 'Optimization', path: '/optimization', icon: Zap, moduleKey: 'optimization' },
            { name: 'CRM', path: '/leads', icon: TrendingUp, moduleKey: 'leads_crm', expandOnly: true, children: [
                { name: 'CRM Workspace', path: '/leads', icon: Users, moduleKey: 'leads_crm' },
                { name: 'Form Builder', path: '/form-intake', icon: ClipboardList, moduleKey: 'form_intake' },
                { name: 'Bookings', path: '/bookings', icon: CalendarClock, moduleKey: 'form_intake' },
            ]},
        ],
    },
    {
        label: 'Content & Creation',
        icon: FileText,
        items: [
            { name: 'Brand Portal', path: '/brand', icon: Palette, moduleKey: 'brand' },
            { name: 'Creator', path: '/content', icon: MessageSquare, moduleKey: 'content' },
            { name: 'Images', path: '/images', icon: Image, moduleKey: 'images' },
            { name: 'Multi-Channel', path: '/multichannel', icon: Share2, moduleKey: 'multichannel' },
            { name: 'Video', path: '/video', icon: Film, moduleKey: 'video' },
            { name: 'Scripts', path: '/scripts', icon: Film, moduleKey: 'scripts' },
            { name: 'Content Catalogue', path: '/catalogue', icon: BookOpen, moduleKey: 'content', children: [
                { name: 'Articles by Stream', path: '/catalogue/articles', icon: FileText, moduleKey: 'content' },
                { name: 'Social Media Posts', path: '/catalogue/social', icon: Share2, moduleKey: 'content' },
                { name: 'SEO Metadata', path: '/catalogue/seo', icon: Search, moduleKey: 'content' },
                { name: 'Emails & Newsletters', path: '/catalogue/newsletters', icon: Mail, moduleKey: 'content' },
                { name: 'Content Calendars', path: '/catalogue/calendars', icon: CalendarDays, moduleKey: 'content' },
            ]},
        ],
    },
    {
        label: 'Marketing & Publishing',
        icon: Megaphone,
        items: [
            { name: 'Campaigns', path: '/campaigns', icon: Megaphone, moduleKey: 'campaigns' },
            { name: 'Ad Copy', path: '/ad-copy', icon: Megaphone, moduleKey: 'campaigns' },
            { name: 'Email Marketing', path: '/edm', icon: Mail, moduleKey: 'edm' },
            { name: 'Organic Growth', path: '/organic-growth', icon: TrendingUp, moduleKey: 'campaigns' },
            { name: 'Lead Forms', path: '/lead-capture', icon: UserPlus, moduleKey: 'lead_capture' },
            { name: 'Quizzes', path: '/quizzes', icon: HelpCircle, moduleKey: 'quizzes' },
            { name: 'Landing Pages', path: '/landing-pages', icon: Rocket, moduleKey: 'landing_pages' },
            { name: 'Reviews', path: '/reviews', icon: Star, moduleKey: 'reviews' },
            { name: 'Scheduling', path: '/scheduling', icon: Calendar, moduleKey: 'scheduling', requiresProject: true },
            { name: 'Social Accounts', path: '/social-accounts', icon: Users, moduleKey: 'campaigns' },
            { name: 'SEO', path: '/seo', icon: Search, moduleKey: 'seo' },
        ],
    },
    {
        label: 'Products & Site',
        icon: ShoppingBag,
        items: [
            { name: 'Marketplace', path: '/marketplace', icon: Store, moduleKey: 'content' },
            { name: 'Products', path: '/products', icon: ShoppingBag, moduleKey: 'products' },
            { name: 'Site Builder', path: '/site-builder', icon: Blocks, moduleKey: 'websites' },
            { name: 'WordPress', path: '/wordpress', icon: Globe, moduleKey: 'wordpress' },
            { name: 'Domains', path: '/domains', icon: Globe, moduleKey: 'domains' },
        ],
    },
    {
        label: 'Automation & AI',
        icon: Bot,
        items: [
            { name: 'AI Agent', path: '/agent', icon: Bot, moduleKey: 'content' },
            { name: 'Coordinator', path: '/coordinator', icon: Radar, moduleKey: 'content' },
            { name: 'Autonomy', path: '/autonomy', icon: Cpu, moduleKey: 'content' },
        ],
    },
    {
        label: 'Administration',
        icon: Settings,
        items: [
            { name: 'Integrations', path: '/integrations', icon: Plug, moduleKey: 'integrations' },
            { name: 'My Teams', path: '/teams', icon: Users, moduleKey: 'teams' },
            { name: 'Email Sending', path: '/admin/smtp', icon: Server, moduleKey: 'dashboard' },
            { name: 'SMS Sending', path: '/settings/sms', icon: MessageSquare, moduleKey: 'sms' },
            { name: 'AI Models', path: '/admin/ai-models', icon: KeyRound, moduleKey: 'dashboard' },
            { name: 'Affiliate', path: '/affiliate', icon: Link2, moduleKey: 'dashboard' },
            { name: 'Users / Admin', path: '/admin', icon: Settings, moduleKey: 'admin', adminOnly: true },
        ],
    },
    {
        label: 'Help & Resources',
        icon: LifeBuoy,
        items: [
            { name: 'Knowledge Base', path: '/knowledge-base', icon: BookOpen, moduleKey: 'dashboard' },
            { name: 'Support Tickets', path: '/tickets', icon: LifeBuoy, moduleKey: 'dashboard' },
        ],
    },
];

// Flat list for backwards compat (used by some lookups)
const navItems: NavItem[] = navSections.flatMap(s => s.items);

// Sentinel "section" id for the user-menu flyout so the collapsed user menu can
// reuse the same flyout state machine (open/close/outside-click/Escape) as the rail.
const USER_MENU_FLYOUT = '__user_menu__';

// Fallback label for a conversation with no title. Formats created_at CLIENT-SIDE
// so it always renders in the viewer's own timezone — do NOT bake a formatted
// timestamp into the title server-side (services.create_conversation used to do
// exactly that, in UTC, permanently — a chat started at 10:53am AEST showed as
// "00:53" for every viewer forever, since the string never got re-evaluated).
const formatUntitledChatLabel = (createdAt: string): string => {
    const d = new Date(createdAt);
    if (isNaN(d.getTime())) return 'Untitled Chat';
    return `Chat ${d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
};

const Layout: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, isAuthenticated, isLoading } = useAuth();
    const { canAccessModule, isAdmin, isTrialUser } = usePermissions();

    // Default to Dark mode for new users, persisted in localStorage
    const [theme, setTheme] = useState<'light' | 'dark'>(
        () => (localStorage.getItem('theme') as 'light' | 'dark') || 'dark'
    );
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [expandedNavItems, setExpandedNavItems] = useState<Set<string>>(new Set());
    const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
        const initial = new Set<string>();
        navSections.forEach(s => { if (s.defaultExpanded) initial.add(s.label); });
        return initial;
    });
    // Tracks sections the user explicitly collapsed — prevents active-route auto-expand from overriding
    const [userCollapsedSections, setUserCollapsedSections] = useState<Set<string>>(new Set());
    const [crmMounted, setCrmMounted] = useState(false);

    // --- Collapsed-rail flyout (HubSpot-style) ---
    // When the sidebar is collapsed on desktop, hovering/clicking a section icon
    // opens a portaled flyout panel with that section's items.
    const [flyoutSection, setFlyoutSection] = useState<string | null>(null);
    const [flyoutAnchorRect, setFlyoutAnchorRect] = useState<DOMRect | null>(null);
    // CRA + @types/node typing: setTimeout returns NodeJS.Timeout, not number — use ReturnType.
    const flyoutCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const flyoutTriggerRef = useRef<HTMLButtonElement | null>(null);
    const navLinksRef = useRef<HTMLDivElement | null>(null);

    // Track real viewport width so grid recalculates on fold/rotate/resize
    // (CSS media queries can be stale on Samsung foldables after fold/unfold)
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth <= 768;
    const isOnboarding = location.pathname.startsWith('/onboarding');

    // Sidebar project section: collapsed by default on all screen sizes
    const [sidebarProjectExpanded, setSidebarProjectExpanded] = useState(false);

    // Real project state
    const [projects, setProjects] = useState<Project[]>([]);
    // Initialise from localStorage so selection survives browser refresh
    const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(
        () => localStorage.getItem('aime_selected_project_id') || undefined
    );

    // Wrapper that keeps localStorage in sync with every intentional project change
    const selectProject = useCallback((id: string | undefined) => {
        setSelectedProjectId(id);
        if (id) {
            localStorage.setItem('aime_selected_project_id', id);
        } else {
            localStorage.removeItem('aime_selected_project_id');
        }
    }, []);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [editingConvId, setEditingConvId] = useState<string | null>(null);
    const [editingConvTitle, setEditingConvTitle] = useState('');
    const [editingDescription, setEditingDescription] = useState(false);
    const [descriptionValue, setDescriptionValue] = useState('');
    const [showArchived, setShowArchived] = useState(false);

    const handleThemeToggle = useCallback(() => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);
    }, [theme]);

    // Set initial theme on mount
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    // Reliable viewport tracking for foldable/rotation support.
    // visualViewport fires more consistently than window.resize on Samsung foldables.
    useEffect(() => {
        const updateWidth = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', updateWidth);
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', updateWidth);
        }
        return () => {
            window.removeEventListener('resize', updateWidth);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', updateWidth);
            }
        };
    }, []);

    // Auto-collapse project section when transitioning to mobile (e.g. folding the phone)
    // Don't auto-expand on desktop — user controls it manually
    useEffect(() => {
        if (isMobile) setSidebarProjectExpanded(false);
    }, [isMobile]);

    // Auto-collapse sidebar when on CRM/Leads page, restore when leaving
    const collapsedBeforeCRM = useRef<boolean | null>(null);
    useEffect(() => {
        if (location.pathname.startsWith('/leads')) {
            if (collapsedBeforeCRM.current === null) {
                collapsedBeforeCRM.current = isCollapsed;
            }
            setIsCollapsed(true);
        } else if (collapsedBeforeCRM.current !== null) {
            setIsCollapsed(collapsedBeforeCRM.current);
            collapsedBeforeCRM.current = null;
        }
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mount CRM iframe early (hidden) so it's pre-loaded when user navigates to /leads
    useEffect(() => {
        if (isAuthenticated && canAccessModule('leads_crm') && !crmMounted) {
            setCrmMounted(true);
        }
    }, [isAuthenticated, crmMounted]); // eslint-disable-line react-hooks/exhaustive-deps

    // Role-based protection: redirect if non-admin tries to access /admin
    useEffect(() => {
        if (user && !isAdmin && location.pathname === '/admin') {
            navigate('/dashboard');
        }
    }, [location.pathname, user, navigate]);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const data = await getProjects();
                setProjects(data);
                const savedId = localStorage.getItem('aime_selected_project_id');
                const savedProject = savedId
                    ? data.find(p => p.project_id === savedId && !p.is_archived)
                    : null;
                if (savedProject) {
                    // Restore the previously selected project
                    setSelectedProjectId(savedProject.project_id);
                } else {
                    // Saved project gone, archived, or the user owns no projects at all.
                    // Clear the stale key AND the in-memory id (seeded from localStorage by
                    // the useState initialiser) so module UIs never render against a phantom
                    // project the user can't act on — e.g. the Webflow connect card firing
                    // /oauth/start with a non-owned project_id and 404ing.
                    localStorage.removeItem('aime_selected_project_id');
                    setSelectedProjectId(undefined);
                    const firstActive = data.find(p => !p.is_archived);
                    if (firstActive) selectProject(firstActive.project_id);
                }
            } catch (error) {
                console.error("Failed to load initial layout data:", error);
            }
        };

        if (isAuthenticated) {
            loadInitialData();
        }
    }, [isAuthenticated, selectedProjectId]);

    // Re-fetch projects when navigating here from onboarding or after project deletion
    useEffect(() => {
        const state = location.state as { refreshProjects?: boolean; selectProjectId?: string } | null;
        if (state?.refreshProjects && isAuthenticated) {
            getProjects().then(data => {
                setProjects(data);
                if (state.selectProjectId) {
                    selectProject(state.selectProjectId);
                } else {
                    const firstActive = data.find((p: any) => !p.is_archived);
                    if (firstActive) selectProject(firstActive.project_id);
                }
            }).catch(() => {});
        }
    }, [location.state, isAuthenticated]);

    // Load conversations when on content page and project is selected
    useEffect(() => {
        if (location.pathname.startsWith('/content') || location.pathname.startsWith('/chat')) {
            const loadConversations = async () => {
                try {
                    const data = await getConversations();
                    // Filter by selected project if one is selected
                    const filtered = selectedProjectId
                        ? data.filter(c => c.project_id === selectedProjectId)
                        : data;
                    setConversations(filtered);
                } catch (error) {
                    console.error("Failed to load conversations:", error);
                }
            };
            loadConversations();
        }
    }, [location.pathname, selectedProjectId]);

    const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        selectProject(e.target.value);
        setEditingDescription(false);
        // Stay on the current page — no redirect to dashboard
    };

    const handleStartEditDescription = () => {
        const project = projects.find(p => p.project_id === selectedProjectId);
        if (project) {
            setDescriptionValue(project.description || '');
            setEditingDescription(true);
        }
    };

    const handleSaveDescription = async () => {
        if (!selectedProjectId) return;
        try {
            await updateProject(selectedProjectId, { description: descriptionValue });
            // Update local state
            setProjects(prev => prev.map(p =>
                p.project_id === selectedProjectId
                    ? { ...p, description: descriptionValue }
                    : p
            ));
            setEditingDescription(false);
        } catch (error) {
            console.error("Failed to update project description:", error);
            alert("Failed to save description.");
        }
    };

    const handleDescriptionKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSaveDescription();
        }
        if (e.key === 'Escape') {
            setEditingDescription(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleMobileNavClose = () => {
        setMobileNavOpen(false);
    };

    // --- Flyout open/close helpers ---
    const cancelFlyoutClose = useCallback(() => {
        if (flyoutCloseTimer.current) {
            clearTimeout(flyoutCloseTimer.current);
            flyoutCloseTimer.current = null;
        }
    }, []);

    // Atomic open/re-anchor: always clears any pending close first, so switching
    // from icon A to icon B works regardless of mouseleave/mouseenter ordering.
    const openFlyout = useCallback((label: string, trigger: HTMLButtonElement) => {
        cancelFlyoutClose();
        flyoutTriggerRef.current = trigger;
        setFlyoutSection(label);
        setFlyoutAnchorRect(trigger.getBoundingClientRect());
    }, [cancelFlyoutClose]);

    const closeFlyout = useCallback(() => {
        cancelFlyoutClose();
        setFlyoutSection(null);
        setFlyoutAnchorRect(null);
    }, [cancelFlyoutClose]);

    // Short delay so the cursor can travel across the gap from rail to panel.
    const scheduleCloseFlyout = useCallback(() => {
        cancelFlyoutClose();
        flyoutCloseTimer.current = setTimeout(() => {
            setFlyoutSection(null);
            setFlyoutAnchorRect(null);
        }, 180);
    }, [cancelFlyoutClose]);

    // Close the flyout on navigation (a flyout link click changes the route).
    useEffect(() => {
        closeFlyout();
    }, [location.pathname, closeFlyout]);

    // The flyout is only meaningful in collapsed-desktop mode — dismiss it the
    // moment the sidebar expands or the viewport drops to mobile.
    useEffect(() => {
        if (!isCollapsed || isMobile) closeFlyout();
    }, [isCollapsed, isMobile, closeFlyout]);

    // Outside-click + Escape close (only while a flyout is open).
    useEffect(() => {
        if (!flyoutSection) return;
        const onDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (navLinksRef.current?.contains(target)) return;
            if (flyoutTriggerRef.current?.contains(target)) return;
            if (document.querySelector('.nav-flyout')?.contains(target)) return;
            closeFlyout();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeFlyout();
                flyoutTriggerRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [flyoutSection, closeFlyout]);

    // Re-measure the anchor rect while a flyout is open so the panel tracks the
    // icon if the rail container scrolls or the window is resized. Low-likelihood
    // in practice (7 icons rarely overflow a viewport, and the flyout closes on
    // mouseleave anyway), but keeps fixed positioning honest without over-engineering.
    useEffect(() => {
        if (!flyoutSection || !flyoutTriggerRef.current) return;
        const remeasure = () => {
            if (flyoutTriggerRef.current) {
                setFlyoutAnchorRect(flyoutTriggerRef.current.getBoundingClientRect());
            }
        };
        const rail = navLinksRef.current;
        rail?.addEventListener('scroll', remeasure);
        window.addEventListener('resize', remeasure);
        return () => {
            rail?.removeEventListener('scroll', remeasure);
            window.removeEventListener('resize', remeasure);
        };
    }, [flyoutSection]);

    // Clear any pending close timer on unmount.
    useEffect(() => () => cancelFlyoutClose(), [cancelFlyoutClose]);

    // Renders one collapsed-rail section icon button.
    const renderRailIcon = (section: NavSection): React.ReactNode => {
        const visibleItems = getVisibleItems(section);
        if (visibleItems.length === 0) return null;
        const SectionIcon = section.icon;
        const sectionActive = visibleItems.some(item =>
            location.pathname === item.path ||
            item.children?.some(c => location.pathname === c.path)
        );
        const isOpen = flyoutSection === section.label;
        return (
            <button
                key={section.label}
                type="button"
                className={`nav-rail-icon ${sectionActive ? 'active' : ''} ${isOpen ? 'flyout-open' : ''}`}
                aria-label={section.label}
                aria-haspopup="true"
                aria-expanded={isOpen}
                aria-current={sectionActive ? 'page' : undefined}
                title={section.label}
                onMouseEnter={(e) => openFlyout(section.label, e.currentTarget)}
                onMouseLeave={scheduleCloseFlyout}
                onClick={(e) => isOpen ? closeFlyout() : openFlyout(section.label, e.currentTarget)}
            >
                <SectionIcon size={22} />
            </button>
        );
    };

    // User avatar (image or initial) — shared by the expanded trigger and the
    // collapsed-rail avatar button.
    const renderUserAvatar = (): React.ReactNode => user && (
        <span className="user-avatar">
            {user.avatar_url ? (
                <img src={user.avatar_url} alt={user.username} />
            ) : (
                <span className="avatar-placeholder">
                    {(user.full_name || user.username).charAt(0).toUpperCase()}
                </span>
            )}
        </span>
    );

    // The user-menu items (Profile / Teams / Sign Out) — shared by the expanded
    // dropdown and the collapsed flyout. Wrapped in <li> for the flyout's <ul>.
    const renderUserMenuItems = (asListItems = false): React.ReactNode => {
        const close = () => { setShowUserMenu(false); handleMobileNavClose(); };
        const wrap = (node: React.ReactNode, key: string) => asListItems ? <li key={key}>{node}</li> : node;
        return (
            <>
                {wrap(
                    <Link to="/profile" className="user-menu-item" onClick={close}>
                        <span className="icon"><User size={18} /></span>
                        <span>My Profile</span>
                    </Link>, 'profile')}
                {wrap(
                    <Link to="/teams" className="user-menu-item" onClick={close}>
                        <span className="icon"><Users size={18} /></span>
                        <span>My Teams</span>
                    </Link>, 'teams')}
                {wrap(<hr className="user-menu-divider" />, 'divider')}
                {wrap(
                    <button onClick={handleLogout} className="user-menu-item logout">
                        <span className="icon"><LogOut size={18} /></span>
                        <span>Sign Out</span>
                    </button>, 'logout')}
            </>
        );
    };

    // Section visibility filter — an item shows if it passes the admin gate and
    // the user can access its module (or is a trial user, who sees locked items).
    // Single source of truth for the accordion, the collapsed rail, and the flyout.
    const getVisibleItems = (section: NavSection): NavItem[] => section.items.filter(item => {
        if (item.adminOnly && !isAdmin) return false;
        const hasAccess = canAccessModule(item.moduleKey);
        return hasAccess || isTrialUser;
    });

    // Renders a single nav item (parent <Link> + its child sub-list).
    // Shared by the expanded accordion and the collapsed-rail flyout so both
    // honour identical permission / lock / requiresProject / submenu behaviour.
    // In flyoutMode the child sub-list is always open (no expand toggle), since
    // the flyout has room to show the full group at once (HubSpot-style).
    const renderNavLink = (item: NavItem, flyoutMode = false): React.ReactNode => {
        const hasAccess = canAccessModule(item.moduleKey);
        const itemPath = item.requiresProject && selectedProjectId
            ? `${item.path}/${selectedProjectId}`
            : item.path;
        const isDisabled = item.requiresProject && !selectedProjectId;
        const isLocked = !hasAccess && isTrialUser;
        const IconComponent = item.icon;
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedNavItems.has(item.name);
        const childActive = hasChildren && item.children!.some(c => location.pathname === c.path);
        const isParentActive = location.pathname === item.path || childActive;
        const showChildren = !isLocked && hasChildren && (flyoutMode || isExpanded || childActive);

        return (
            <li key={item.name}>
                <Link
                    to={isLocked ? '/billing' : itemPath}
                    className={`nav-item ${isParentActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''} ${isLocked ? 'nav-item-locked' : ''}`}
                    aria-current={isParentActive ? 'page' : undefined}
                    onClick={(e) => {
                        if (isLocked) { handleMobileNavClose(); return; }
                        if (isDisabled) { e.preventDefault(); return; }
                        if (hasChildren && !flyoutMode) {
                            // expandOnly parents (e.g. CRM) toggle their submenu without navigating —
                            // so the /leads auto-collapse fires only when "CRM Workspace" is chosen.
                            if (item.expandOnly) e.preventDefault();
                            setExpandedNavItems(prev => {
                                const next = new Set(prev);
                                if (next.has(item.name)) next.delete(item.name);
                                else next.add(item.name);
                                return next;
                            });
                        }
                        handleMobileNavClose();
                    }}
                    title={isLocked ? 'Upgrade to unlock' : isDisabled ? 'Select a project first' : undefined}
                >
                    <span className="icon"><IconComponent size={20} /></span>
                    <span className="text">{item.name}</span>
                    {isLocked && <Lock size={12} style={{ marginLeft: 'auto', opacity: 0.5 }} />}
                    {!isLocked && hasChildren && !flyoutMode && (
                        <span className="submenu-chevron">
                            {(isExpanded || childActive) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </span>
                    )}
                </Link>
                {showChildren && (
                    <ul className="nav-sub-list">
                        {item.children!.map(child => {
                            const ChildIcon = child.icon;
                            return (
                                <li key={child.name}>
                                    <Link
                                        to={child.path}
                                        className={`nav-sub-item ${location.pathname === child.path ? 'active-sub' : ''}`}
                                        onClick={handleMobileNavClose}
                                    >
                                        <span className="icon"><ChildIcon size={16} /></span>
                                        <span className="text">{child.name}</span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </li>
        );
    };

    const handleRenameConversation = async (conversationId: string) => {
        const newTitle = editingConvTitle.trim();
        if (!newTitle) {
            setEditingConvId(null);
            return;
        }
        try {
            await updateConversation(conversationId, newTitle);
            setConversations(prev => prev.map(c =>
                c.conversation_id === conversationId ? { ...c, title: newTitle } : c
            ));
        } catch (err) {
            console.error('Failed to rename conversation:', err);
        }
        setEditingConvId(null);
    };

    const handleDeleteConversation = async (conversationId: string) => {
        try {
            await deleteConversation(conversationId);
            setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
            if (location.pathname.includes(conversationId)) {
                navigate('/content');
            }
        } catch (error) {
            console.error('Failed to delete conversation:', error);
        }
    };

    // Close mobile nav when route changes
    useEffect(() => {
        setMobileNavOpen(false);
    }, [location.pathname]);

    // Auto-collapse sections that don't contain the active route
    useEffect(() => {
        const activeSection = navSections.find(section =>
            section.items.some(item =>
                location.pathname === item.path ||
                location.pathname.startsWith(item.path + '/') ||
                item.children?.some(c => location.pathname === c.path)
            )
        );
        if (!activeSection) return;
        // Allow the active section to auto-expand (clear any explicit collapse on it)
        setUserCollapsedSections(prev => {
            const next = new Set(prev);
            next.delete(activeSection.label);
            return next;
        });
        // Collapse all other sections that were manually expanded
        setExpandedSections(prev => {
            const next = new Set(prev);
            navSections.forEach(s => {
                if (s.label !== activeSection.label) next.delete(s.label);
            });
            return next;
        });
    }, [location.pathname]);

    // Show loading state
    if (isLoading) {
        return (
            <div className="app-layout loading">
                <div className="loading-spinner">Loading...</div>
            </div>
        );
    }

    return (
        <div
            className={`app-layout ${theme} ${isCollapsed ? 'sidebar-collapsed' : ''}`}
            style={{ gridTemplateColumns: isOnboarding || isMobile ? '1fr' : isCollapsed ? '60px 1fr' : '280px 1fr' }}
        >
            {!isOnboarding && <>
            {/* Hamburger Menu Button (mobile only) */}
            <button
                className="hamburger-btn"
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                aria-label="Toggle navigation menu"
            >
                {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Mobile Nav Overlay */}
            <div
                className={`mobile-nav-overlay ${mobileNavOpen ? 'visible' : ''}`}
                onClick={() => setMobileNavOpen(false)}
            />

            <nav className={`sidebar ${mobileNavOpen ? 'mobile-open' : ''}`}>
                <button
                    className="sidebar-toggle"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                >
                    {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                </button>

                <div className="sidebar-header">
                    <div className="sidebar-project-label-row">
                        <button
                            className="project-label-toggle"
                            onClick={() => setSidebarProjectExpanded(e => !e)}
                        >
                            <span className="project-collapse-chevron">
                                {sidebarProjectExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </span>
                            <span className="project-label">Project:</span>
                        </button>
                        <button
                            className="new-project-btn"
                            onClick={() => navigate('/projects/new')}
                            title="Create New Project"
                        >
                            <Plus size={16} />
                        </button>
                    </div>
                    {sidebarProjectExpanded && (
                    <>
                    <div className="project-context">
                        <div className="project-info">
                            {selectedProjectId && (
                                <Link
                                    to={`/project/${selectedProjectId}/settings`}
                                    className="settings-icon"
                                    title="Project Settings"
                                    onClick={handleMobileNavClose}
                                >
                                    <Settings size={16} />
                                </Link>
                            )}
                            <select
                                value={selectedProjectId || ''}
                                onChange={handleProjectChange}
                                className="project-selector"
                            >
                                <option value="">Select Project...</option>
                                {projects.filter(p => !p.is_archived).map(proj => (
                                    <option key={proj.project_id} value={proj.project_id}>{proj.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Show archived projects section below project description */}
                    {projects.filter(p => p.is_archived).length > 0 && (
                        <div className="archived-section">
                            <label
                                className="show-archived-toggle"
                                onClick={() => setShowArchived(!showArchived)}
                            >
                                {showArchived ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Archived Projects ({projects.filter(p => p.is_archived).length})
                            </label>
                            {showArchived && (
                                <ul className="archived-list">
                                    {projects.filter(p => p.is_archived).map(proj => (
                                        <li key={proj.project_id} className="archived-item">
                                            <button
                                                className="restore-btn"
                                                onClick={async () => {
                                                    try {
                                                        await archiveProject(proj.project_id, false);
                                                        // Reload projects
                                                        const data = await getProjects();
                                                        setProjects(data);
                                                    } catch (error) {
                                                        console.error('Failed to restore project:', error);
                                                    }
                                                }}
                                                title="Restore project"
                                            >
                                                <RotateCcw size={14} />
                                            </button>
                                            <span
                                                className="archived-name"
                                                onClick={() => selectProject(proj.project_id)}
                                            >
                                                {proj.name}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    </>
                    )}
                </div>

                <div className="nav-links" ref={navLinksRef}>
                    {(isCollapsed && !isMobile)
                        ? navSections.map(section => renderRailIcon(section))
                        : navSections.map(section => {
                        const visibleItems = getVisibleItems(section);
                        if (visibleItems.length === 0) return null;

                        // Auto-expand section if any child route is active
                        const sectionHasActiveRoute = visibleItems.some(item => {
                            if (location.pathname === item.path) return true;
                            if (item.children?.some(c => location.pathname === c.path)) return true;
                            return false;
                        });
                        // Auto-expand if active route, but respect user's explicit collapse
                        const isSectionOpen = userCollapsedSections.has(section.label)
                            ? false
                            : expandedSections.has(section.label) || sectionHasActiveRoute;

                        const toggleSection = () => {
                            if (isSectionOpen) {
                                // User is collapsing — track it so active-route doesn't force re-open
                                setExpandedSections(prev => { const next = new Set(prev); next.delete(section.label); return next; });
                                setUserCollapsedSections(prev => new Set(prev).add(section.label));
                            } else {
                                // User is expanding — clear the explicit collapse
                                setExpandedSections(prev => new Set(prev).add(section.label));
                                setUserCollapsedSections(prev => { const next = new Set(prev); next.delete(section.label); return next; });
                            }
                        };

                        return (
                            <div key={section.label} className={`nav-section ${isSectionOpen ? 'nav-section--open' : ''}`}>
                                <button
                                    className="nav-section-label"
                                    onClick={toggleSection}
                                    type="button"
                                >
                                    <span className="nav-section-label-text">{section.label}</span>
                                    <span className="nav-section-chevron">
                                        {isSectionOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                    </span>
                                </button>
                                {isSectionOpen && (
                                <ul className="nav-section-items">
                                    {visibleItems.map(item => renderNavLink(item))}
                                </ul>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Collapsed-rail flyout panel (portaled to body to escape sidebar overflow) */}
                {(isCollapsed && !isMobile) && flyoutSection && flyoutAnchorRect && (() => {
                    // User-menu flyout (Profile / Teams / Sign Out).
                    if (flyoutSection === USER_MENU_FLYOUT) {
                        if (!user) return null;
                        return (
                            <NavFlyout
                                title={user.full_name || user.username}
                                anchorRect={flyoutAnchorRect}
                                onMouseEnter={cancelFlyoutClose}
                                onMouseLeave={scheduleCloseFlyout}
                            >
                                {renderUserMenuItems(true)}
                            </NavFlyout>
                        );
                    }
                    // Section flyout.
                    const section = navSections.find(s => s.label === flyoutSection);
                    if (!section) return null;
                    const items = getVisibleItems(section);
                    if (items.length === 0) return null;
                    return (
                        <NavFlyout
                            title={section.label}
                            anchorRect={flyoutAnchorRect}
                            onMouseEnter={cancelFlyoutClose}
                            onMouseLeave={scheduleCloseFlyout}
                        >
                            {items.map(item => renderNavLink(item, true))}
                        </NavFlyout>
                    );
                })()}

                {/* Recent Conversations (Only show on Content tab) */}
                {(location.pathname.startsWith('/content') || location.pathname.startsWith('/chat')) && (
                    <div className="sidebar-section">
                        <h4 className="sidebar-section-title">Recent Chats</h4>
                        <ul className="sidebar-list">
                            <li className="sidebar-list-item new-chat">
                                <Link to="/content" className="nav-item" onClick={handleMobileNavClose}>
                                    <span className="icon"><Plus size={18} /></span>
                                    <span className="text">New Chat</span>
                                </Link>
                            </li>
                            {conversations.slice(0, 5).map(conv => (
                                <li key={conv.conversation_id} className="sidebar-list-item">
                                    {editingConvId === conv.conversation_id ? (
                                        <div className="nav-item" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span className="icon"><MessageSquare size={18} /></span>
                                            <input
                                                className="conv-rename-input"
                                                value={editingConvTitle}
                                                onChange={e => setEditingConvTitle(e.target.value)}
                                                onBlur={() => handleRenameConversation(conv.conversation_id)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') handleRenameConversation(conv.conversation_id);
                                                    if (e.key === 'Escape') setEditingConvId(null);
                                                }}
                                                autoFocus
                                            />
                                        </div>
                                    ) : (
                                        <Link
                                            to={`/chat/${conv.conversation_id}`}
                                            className={`nav-item ${location.pathname.includes(conv.conversation_id) ? 'active-sub' : ''}`}
                                            onClick={handleMobileNavClose}
                                            onDoubleClick={(e) => {
                                                e.preventDefault();
                                                setEditingConvId(conv.conversation_id);
                                                setEditingConvTitle(conv.title || '');
                                            }}
                                        >
                                            <span className="icon"><MessageSquare size={18} /></span>
                                            <span className="text truncate">{conv.title || formatUntitledChatLabel(conv.created_at)}</span>
                                        </Link>
                                    )}
                                    <button
                                        className="chat-delete-btn"
                                        onClick={() => handleDeleteConversation(conv.conversation_id)}
                                        title="Delete conversation"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="sidebar-footer">
                    {/* Trial mode badge */}
                    {isTrialUser && (
                        <Link
                            to="/billing"
                            className="trial-badge"
                            title="Upgrade your plan"
                            onClick={handleMobileNavClose}
                        >
                            <Lock size={12} />
                            <span className="text">Trial mode · Upgrade</span>
                        </Link>
                    )}

                    {/* Theme Toggle */}
                    <button
                        onClick={handleThemeToggle}
                        className="theme-toggle-btn"
                        title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
                    >
                        <span className="theme-icon">{theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}</span>
                        <span className="theme-text">{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
                    </button>

                    {/* User menu */}
                    {user && ((isCollapsed && !isMobile) ? (
                        // Collapsed-rail: avatar icon opening a portaled flyout (HubSpot-style),
                        // reusing the same flyout state machine as the section rail.
                        <div className="user-menu-container">
                            <button
                                type="button"
                                className={`user-menu-trigger user-menu-trigger--rail ${flyoutSection === USER_MENU_FLYOUT ? 'flyout-open' : ''}`}
                                aria-haspopup="true"
                                aria-expanded={flyoutSection === USER_MENU_FLYOUT}
                                aria-label="User menu"
                                title={user.full_name || user.username}
                                onMouseEnter={(e) => openFlyout(USER_MENU_FLYOUT, e.currentTarget)}
                                onMouseLeave={scheduleCloseFlyout}
                                onClick={(e) => flyoutSection === USER_MENU_FLYOUT ? closeFlyout() : openFlyout(USER_MENU_FLYOUT, e.currentTarget)}
                            >
                                {renderUserAvatar()}
                            </button>
                        </div>
                    ) : (
                        <div className="user-menu-container" style={{ marginTop: '12px' }}>
                            <button
                                className="user-menu-trigger"
                                onClick={() => setShowUserMenu(!showUserMenu)}
                            >
                                {renderUserAvatar()}
                                <span className="user-info">
                                    <span className="user-name">{user.full_name || user.username}</span>
                                    <span className="user-role">{user.role}</span>
                                </span>
                            </button>

                            {showUserMenu && (
                                <div className="user-menu-dropdown">
                                    {renderUserMenuItems()}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </nav>
            </>}

            <main className={`content-area${/\/landing-pages\/[^/]+\/edit/.test(location.pathname) || /\/site-builder\/(?!new($|\/))[^/]+/.test(location.pathname) ? ' content-area--editor' : ''}`}>
                <div style={{ display: location.pathname === '/leads' ? 'none' : undefined }}>
                    <Outlet context={{ selectedProjectId, onProjectSelect: selectProject }} />
                </div>
                {canAccessModule('leads_crm') && crmMounted && (
                    <div style={{ display: location.pathname === '/leads' ? undefined : 'none', height: '100%' }}>
                        <CRMWorkspace
                            selectedProjectId={selectedProjectId}
                            projects={projects}
                            onProjectSelect={selectProject}
                            isVisible={location.pathname === '/leads'}
                        />
                    </div>
                )}
                {/* Structural bottom spacer — real DOM child so flex layout always
                    reserves space. Pseudo-element ::after is taken by the light-mode
                    noise overlay (see Layout.css), which won the cascade and pulled
                    the previous spacer out of flex flow. */}
                <div className="content-area-spacer" aria-hidden="true" />
            </main>

            {/* Restricted billing banner — sticky top, shown when status is past_due/unpaid/paused */}
            {!isOnboarding && <RestrictedBanner />}

            {/* H2.11 Contextual Page Assistant — floating widget, position:fixed, outside grid flow */}
            <AssistantWidget />

            {/* Notification bell — dashboard only, avoids blocking module-level UI */}
            {location.pathname === '/dashboard' && (
                <div style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 1050 }}>
                    <NotificationCentre />
                </div>
            )}

        </div>
    );
};

export default Layout;
